import React, { useState, useEffect, useRef } from "react";
import { RiLockPasswordLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "profileSidebarActiveMenu";

const ProfileSidebar = ({
  onMenuChange,
  activeMenu: propActiveMenu,
  toggleTheme,
  isDarkMode,
}) => {
  // Load last active menu from localStorage, default to 1 (Profile)
  const getInitialMenu = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const menuId = parseInt(saved, 10);
        // Don't restore Dark Theme (7) or Logout (8) as active
        if (menuId && menuId !== 7 && menuId !== 8) {
          return menuId;
        }
      }
    } catch (e) {
      // ignore localStorage errors
    }
    return propActiveMenu || 1;
  };

  const [activeMenu, setActiveMenu] = useState(getInitialMenu);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const hasProcessedUrlParams = useRef(false);
  const hasInitialized = useRef(false);
  const navigate = useNavigate();

  // On mount, notify parent of the restored menu
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (onMenuChange && activeMenu !== 1) {
        onMenuChange(activeMenu);
      }
    }
  }, []);

  // Save active menu to localStorage whenever it changes
  useEffect(() => {
    // Don't save Dark Theme (7) or Logout (8)
    if (activeMenu && activeMenu !== 7 && activeMenu !== 8) {
      try {
        localStorage.setItem(STORAGE_KEY, activeMenu.toString());
      } catch (e) {
        // ignore localStorage errors
      }
    }
  }, [activeMenu]);

  // Sync internal state when propActiveMenu changes
  useEffect(() => {
    if (propActiveMenu && propActiveMenu !== activeMenu) {
      setActiveMenu(propActiveMenu);
    }
  }, [propActiveMenu]);

  // If the URL contains specific query params, activate the Google Services menu.
  useEffect(() => {
    if (hasProcessedUrlParams.current) return;

    try {
      const params = new URLSearchParams(window.location.search);
      const type = params.get("type");
      const success = params.get("success");

      if (type && success) {
        hasProcessedUrlParams.current = true;
        setActiveMenu(2);
        if (onMenuChange) onMenuChange(2);
      }
    } catch (e) {
      // ignore
    }
  }, [onMenuChange]);

  // Menu sections with items
  const menuSections = [
    {
      title: "Account",
      items: [
        {
          id: 1,
          label: "Profile",
          icon: "bi bi-person-fill",
        },
        {
          id: 13,
          label: "Contacts",
          icon: "bi bi-people-fill",
        },
      ],
    },
    {
      title: "Features",
      items: [
        {
          id: 11,
          label: "Knowledge Base",
          icon: "bi bi-lightbulb-fill",
        },
        {
          id: 12,
          label: "Second Brain",
          icon: "bi bi-journal-bookmark-fill",
        },
      ],
    },
    {
      title: "Connections",
      items: [
        {
          id: 2,
          label: "Integrations",
          icon: "bi bi-plug-fill",
        },
      ],
    },
    {
      title: "Settings",
      items: [
        {
          id: 15,
          label: "Billing",
          icon: "bi bi-credit-card-fill",
        },
        {
          id: 16,
          label: "Credit Usage",
          icon: "bi bi-bar-chart-fill",
        },
        {
          id: 7,
          label: isDarkMode ? "Dark Mode" : "Light Mode",
          icon: isDarkMode ? "bi bi-moon-stars-fill" : "bi bi-sun-fill",
        },
        {
          id: 4,
          label: "Change Password",
          icon: <RiLockPasswordLine size={18} />,
        },
        {
          id: 6,
          label: "Usage",
          icon: "bi bi-graph-up",
          adminOnly: true,
        },
        {
          id: 14,
          label: "Query Analyzer",
          icon: "bi bi-search",
          adminOnly: true,
        },
      ],
    },
  ];

  // Logout item (separate)
  const logoutItem = {
    id: 8,
    label: "Logout",
    icon: "bi bi-box-arrow-right",
    isDanger: true,
  };

  const handleMenuClick = (menuId, item) => {
    // Check if this menu item has a route (like Integrations)
    if (item?.isRoute && item?.route) {
      navigate(item.route, { state: { from: window.location.pathname } });
      return;
    }

    // Don't set Dark Theme toggle (7) as active menu
    if (menuId !== 7) {
      setActiveMenu(menuId);
    }
    if (onMenuChange) {
      onMenuChange(menuId);
    }
  };

  const getMenuStyle = (item) => {
    const isActive = activeMenu === item.id && item.id !== 7 && item.id !== 8;
    const isHovered = hoveredMenu === item.id;

    let backgroundColor = "transparent";
    let color = "var(--text)";

    if (isActive) {
      backgroundColor = "var(--teal-bg)";
      color = "var(--textwhite)";
    } else if (isHovered && !item.isDanger) {
      backgroundColor = "rgba(0, 150, 137, 0.08)";
      color = "var(--teal-bg)";
    } else if (isHovered && item.isDanger) {
      backgroundColor = "rgba(231, 0, 11, 0.08)";
      color = "var(--danger)";
    } else if (item.isDanger) {
      color = "var(--text)";
    }

    return {
      cursor: "pointer",
      color,
      background: backgroundColor,
      transition: "all 0.2s ease",
      borderRadius: "12px",
      padding: "11px 14px",
      marginBottom: "4px",
    };
  };

  const getIconStyle = (item) => {
    const isActive = activeMenu === item.id && item.id !== 7 && item.id !== 8;
    const isHovered = hoveredMenu === item.id;

    let backgroundColor = "var(--input-box-bg)";
    let color = "var(--text)";

    if (isActive) {
      backgroundColor = "rgba(255, 255, 255, 0.2)";
      color = "var(--textwhite)";
    } else if (isHovered && !item.isDanger) {
      backgroundColor = "rgba(0, 150, 137, 0.15)";
      color = "var(--teal-bg)";
    } else if (isHovered && item.isDanger) {
      backgroundColor = "rgba(231, 0, 11, 0.15)";
      color = "var(--danger)";
    } else if (item.isDanger) {
      backgroundColor = "rgba(231, 0, 11, 0.08)";
      color = "var(--danger)";
    }

    return {
      width: "34px",
      height: "34px",
      borderRadius: "10px",
      backgroundColor,
      color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "12px",
      transition: "all 0.2s ease",
      fontSize: "16px",
    };
  };

  return (
    <div
      style={{
        width: "260px",
        background: "var(--background)",
        borderRight: "1px solid var(--border-color)",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "20px 16px",
          borderBottom: "1px solid var(--border-color)",
          height: "81px",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          {/* Logo */}
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0, 150, 137, 0.25)",
            }}
          >
            <i className="bi bi-gear-fill" style={{ color: "white", fontSize: "18px" }}></i>
          </div>
          <div>
            <h6
              style={{
                margin: 0,
                color: "var(--text)",
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              Settings
            </h6>
            <span
              style={{
                color: "var(--text)",
                opacity: 0.5,
                fontSize: "12px",
              }}
            >
              Manage your account
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 12px",
        }}
      >
        {menuSections.map((section, sectionIndex) => (
          <div key={section.title} style={{ marginBottom: "20px" }}>
            {/* Section Title */}
            <div
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text)",
                opacity: 0.45,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                padding: "0 14px",
                marginBottom: "8px",
              }}
            >
              {section.title}
            </div>

            {/* Section Items */}
            {section.items.map((item) => (
              <div
                key={item.id}
                style={getMenuStyle(item)}
                onClick={() => handleMenuClick(item.id, item)}
                onMouseEnter={() => setHoveredMenu(item.id)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <div className="d-flex align-items-center w-100">
                  {/* Icon */}
                  <div style={getIconStyle(item)}>
                    {typeof item.icon === "string" ? (
                      <i className={item.icon}></i>
                    ) : (
                      item.icon
                    )}
                  </div>

                  {/* Label */}
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: activeMenu === item.id && item.id !== 7 ? 600 : 500,
                      flex: 1,
                    }}
                  >
                    {item.label}
                  </span>

                  {/* Theme Toggle Switch */}
                  {item.id === 7 && (
                    <label
                      className="theme-switch"
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginLeft: "auto" }}
                    >
                      <input
                        type="checkbox"
                        checked={isDarkMode}
                        onChange={toggleTheme}
                        style={{ display: "none" }}
                      />
                      <span className="slider"></span>
                    </label>
                  )}

                  {/* Arrow for navigation items */}
                  {item.isRoute && (
                    <i
                      className="bi bi-chevron-right"
                      style={{
                        fontSize: "12px",
                        opacity: 0.5,
                        marginLeft: "auto",
                      }}
                    ></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer - Logout */}
      <div
        style={{
          padding: "16px 12px",
          borderTop: "1px solid var(--border-color)",
        }}
      >
        <div
          style={getMenuStyle(logoutItem)}
          onClick={() => handleMenuClick(logoutItem.id, logoutItem)}
          onMouseEnter={() => setHoveredMenu(logoutItem.id)}
          onMouseLeave={() => setHoveredMenu(null)}
        >
          <div className="d-flex align-items-center w-100">
            <div style={getIconStyle(logoutItem)}>
              <i className={logoutItem.icon}></i>
            </div>
            <span
              style={{
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              {logoutItem.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSidebar;
